import { CULTURE_OUTCOMES, SUSCEPTIBILITY_RESULTS } from '@wodi/domain';
import { z } from 'zod';

import { actorSchema } from './clinical-common.ts';

// Microbiology (docs/plans/phase3-lab-qc-and-microbiology.md §1.2, §3; ADR 0039). Organisms
// and antibiotics are tenant lists; a culture is rows on the report version. S, I and R are
// stored as the lab entered them; nothing here interprets them. Comments are PHI and travel
// only in bodies.

export const LAB_MICRO_LISTS = ['organisms', 'antibiotics'] as const;
export type LabMicroList = (typeof LAB_MICRO_LISTS)[number];

/** A code from an official file, when one is loaded (TODO(verify-spec): none yet). */
export const labMicroCodeSchema = z.object({
  system: z.string(),
  version: z.string(),
  code: z.string(),
});

export const labMicroItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  code: labMicroCodeSchema.nullable(),
  status: z.enum(['active', 'inactive']),
});
export type LabMicroItemView = z.infer<typeof labMicroItemSchema>;

export const labMicroListSchema = z.object({ items: z.array(labMicroItemSchema) });

const microNameSchema = z.string().trim().min(1).max(120);

export const labMicroItemInputSchema = z.object({ name: microNameSchema });

export const updateLabMicroItemSchema = z
  .object({
    name: microNameSchema.optional(),
    status: z.enum(['active', 'inactive']).optional(),
  })
  .refine((v) => v.name !== undefined || v.status !== undefined, {
    message: 'Change the name or the status.',
  });

// --- Cultures -------------------------------------------------------------------------------

const commentSchema = z.string().trim().max(1000);

export const cultureSusceptibilitySchema = z.object({
  antibioticId: z.uuid(),
  antibioticName: z.string(),
  code: labMicroCodeSchema.nullable(),
  result: z.enum(SUSCEPTIBILITY_RESULTS),
  /** As a person reads it: "≤0.25", ">32"; null when not entered. */
  mic: z.string().nullable(),
  zoneMm: z.number().int().nullable(),
});
export type CultureSusceptibilityView = z.infer<typeof cultureSusceptibilitySchema>;

export const cultureIsolateSchema = z.object({
  isolateNumber: z.number().int().min(1),
  organismId: z.uuid(),
  organismName: z.string(),
  code: labMicroCodeSchema.nullable(),
  quantity: z.string().nullable(),
  comment: z.string().nullable(),
  susceptibilities: z.array(cultureSusceptibilitySchema),
});
export type CultureIsolateView = z.infer<typeof cultureIsolateSchema>;

export const cultureSchema = z.object({
  setUpAt: z.iso.datetime().nullable(),
  setUpNote: z.string().nullable(),
  outcome: z.enum(CULTURE_OUTCOMES),
  /** The lab marked this culture critical (plan §1.2.7). */
  critical: z.boolean(),
  comment: z.string().nullable(),
  isolates: z.array(cultureIsolateSchema),
  enteredBy: z.object({ id: z.uuid(), displayName: z.string() }).nullable(),
  enteredAt: z.iso.datetime().nullable(),
});
export type CultureView = z.infer<typeof cultureSchema>;

export const saveCultureSchema = z.object({
  expectedRevision: z.number().int().min(1),
  setUpAt: z.iso.datetime({ offset: true }).nullable().default(null),
  setUpNote: z.string().trim().max(500).nullable().default(null),
  outcome: z.enum(CULTURE_OUTCOMES),
  critical: z.boolean().default(false),
  comment: commentSchema.nullable().default(null),
  isolates: z
    .array(
      z.object({
        isolateNumber: z.number().int().min(1).max(10),
        organismId: z.uuid(),
        quantity: z.string().trim().max(100).nullable().default(null),
        comment: commentSchema.nullable().default(null),
        susceptibilities: z
          .array(
            z.object({
              antibioticId: z.uuid(),
              result: z.enum(SUSCEPTIBILITY_RESULTS),
              mic: z.string().trim().max(20).nullable().default(null),
              zoneMm: z.number().int().min(0).max(999).nullable().default(null),
            }),
          )
          .max(40)
          .default([]),
      }),
    )
    .max(10)
    .default([]),
});
export type SaveCultureBody = z.input<typeof saveCultureSchema>;

/** A preliminary report: the culture as it stood when issued, marked Preliminary. */
export const preliminaryCultureReportSchema = z.object({
  id: z.uuid(),
  sequence: z.number().int().min(1),
  issuedAt: z.iso.datetime(),
  issuedBy: actorSchema,
  critical: z.boolean(),
  culture: cultureSchema,
});
export type PreliminaryCultureReportView = z.infer<typeof preliminaryCultureReportSchema>;

/** What a clinician sees of a preliminary report in the patient record. */
export const patientPreliminaryReportSchema = preliminaryCultureReportSchema.extend({
  reportId: z.uuid(),
  orderId: z.uuid(),
  testName: z.string(),
  sampleNumber: z.string(),
  specimenType: z.string(),
  collectedAt: z.iso.datetime(),
  requester: z.string().nullable(),
});
export type PatientPreliminaryReportView = z.infer<typeof patientPreliminaryReportSchema>;
